import { Component, Inject, OnInit } from '@angular/core';
import { BaseComponent } from '../../../shared/base-classes/base.component';
import { AppToastManagerService } from '../../../shared/services/toast-manager.service';
import { AppBehaviorCodeEntryService } from '../behavior-code-entry.service';
import {
  BehaviorCodeEntryCreateDto,
  BehaviorCodeEntryResponseDto,
  IRelation,
  PrivilegeEnum,
  ReactionTypeEnum,
  StudentDetailResponseDto
} from '@whetstoneeducation/hero-common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { IBehaviorCodeEntryCreateFormControlData } from './behavior-code-entry-create.models';
import { AppStudentsService } from '../../students/students.service';
import { Router } from '@angular/router';
import { AppPrivilegesService } from '../../auth/privileges.service';
import { map, Observable, startWith } from 'rxjs';

@Component({
  selector: 'app-behavior-code-entry-create-modal',
  templateUrl: './behavior-code-entry-create-modal.template.html',
  styleUrls: ['./behavior-code-entry-create-modal.scss']
})
export class AppBehaviorCodeEntryCreateModalComponent
  extends BaseComponent
  implements OnInit {
  public title: string = 'Behavior Tracking: ';
  public students: StudentDetailResponseDto[] = [];
  public behaviorCodeEntries: BehaviorCodeEntryResponseDto[] = [];
  public isLoading = false;
  public behaviorCodeTypeOptions: IRelation[];
  public behaviorCodeOptions: IRelation[];
  public behaviorCodeReactionOptions: IRelation[];
  public behaviorCodeTypeIdControl = new FormControl<IRelation>(
    null,
    Validators.required
  );
  public behaviorCodeIdControl = new FormControl<IRelation>(
    null,
    Validators.required
  );
  public behaviorCodeReactionIdControl = new FormControl<IRelation>(
    null,
    Validators.required
  );
  public notesControl = new FormControl<string>('');

  public behaviorCodeEntryForms: FormGroup<IBehaviorCodeEntryCreateFormControlData>[];

  public onClose: () => void;

  public isSubmitDisabled = true;

  public filteredBehaviorTypes: Observable<IRelation[]>;
  public filteredBehaviorCodes: Observable<IRelation[]>;
  public filteredReactions: Observable<IRelation[]>;

  constructor(
    public toastService: AppToastManagerService,
    public behaviorCodeEntryService: AppBehaviorCodeEntryService,
    public studentsService: AppStudentsService,
    public privilegesService: AppPrivilegesService,
    public dialogRef: MatDialogRef<AppBehaviorCodeEntryCreateModalComponent>,
    public formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      studentIds: number[];
      onClose?: () => void;
    },
    public router: Router
  ) {
    super({ privilegesService });
    this.data.studentIds.map((studentId) => {
      this.studentsService
        .getStudentById(studentId)
        .then((student) => {
          this.students.push(student);
        })
        .catch((error) => {
          this.toastService.error(error.message);
          this.dialogRef.close();
        });
    });
    if (this.data.onClose) {
      this.onClose = this.data.onClose;
    }
  }

  async ngOnInit() {
    this.isLoading = true;
    try {
      const user = this.StorageManager.getLoggedInUser();
      this.behaviorCodeTypeOptions =
        await this.behaviorCodeEntryService.getBehaviorCodeTypesWithBehaviorCodes(
          user.schoolGroupId
        );
    } catch (error) {
      this.toastService.error(error.message);
      this.dialogRef.close();
    }

    this.isLoading = false;
    this.updateIsSubmitDisabled();

    this.behaviorCodeTypeIdControl.valueChanges.subscribe(async (value) => {
      if (value?.id) {
        this.isLoading = true;
        try {
          this.behaviorCodeOptions =
            await this.behaviorCodeEntryService.getBehaviorCodesByType(value.id);
          this.title = 'Select a Behavior Code: ';
        } catch (error) {
          this.toastService.error(error.message);
          this.dialogRef.close();
        }
        this.isLoading = false;
        this.updateIsSubmitDisabled();
      } else {
        this.behaviorCodeOptions = [];
      }
    });

    this.filteredBehaviorTypes = this.behaviorCodeTypeIdControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const label = typeof value === 'string' ? value : value.name;
        return this._filter(label, 'behaviorCodeTypeOptions');
      })
    );

    this.behaviorCodeIdControl.valueChanges.subscribe(async (value) => {
      if (!value?.id) {
        return;
      }
      this.isLoading = true;
      try {
        if (this.students.length > 0) {
          this.behaviorCodeReactionOptions =
            await this.behaviorCodeEntryService.getReactionOptions(
              value.id,
              this.students[0].id,
              ReactionTypeEnum.SELECT_MENU
            );
        }
      } catch (error) {
        this.toastService.error(error?.message || 'Failed to load reaction options');
        this.dialogRef.close();
      }

      this.behaviorCodeEntryForms = this.students.map((student) => {
        return this.formBuilder.group({
          behaviorCodeControl: [
            this.behaviorCodeOptions.find(
              (behaviorCode) => behaviorCode.id === value.id
            ),
            Validators.required
          ],
          studentControl: [student, Validators.required],
          reactionControl: [
            {
              id: -1,
              name: 'Loading...'
            },
            Validators.required
          ],
          scheduledReactionControl: [
            {
              id: -1,
              name: 'Loading...'
            }
          ],
          dateControl: [new Date(), Validators.required],
          notesControl: [this.notesControl.value]
        });
      });
      this.title = 'Confirm Reactions: ';
      this.isLoading = false;

      this.updateIsSubmitDisabled();
    });

    this.filteredBehaviorCodes = this.behaviorCodeIdControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const label = typeof value === 'string' ? value : value.name;
        return this._filter(label, 'behaviorCodeOptions');
      })
    );

    this.filteredReactions = this.behaviorCodeReactionIdControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const label = typeof value === 'string' ? value : value.name;
        return this._filter(label, 'behaviorCodeReactionOptions');
      })
    );

    this.behaviorCodeReactionIdControl.valueChanges.subscribe(() => {
      this.updateIsSubmitDisabled();
    });

    this.notesControl.valueChanges.subscribe(() => {
      this.updateIsSubmitDisabled();
    });
  }

  private _filter(value: string, options: string): IRelation[] {
    if (!this[options]) {
      return [];
    } else if (!value) {
      return this[options];
    }

    const filterValue = value?.toLowerCase();

    return this[options].filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  displaySelectedOption(options: string, optionSelected: IRelation | null) {
    const selected = this[options].find(item => item.id === optionSelected?.id);
    return selected?.name || '';
  }

  public onClear(control: string) {
    this[control].setValue('');
  }

  async submit() {
    this.isLoading = true;
    try {
      const entries = this.behaviorCodeEntryForms.map((form) => {
        return new BehaviorCodeEntryCreateDto({
          notes: this.notesControl.value,
          studentId: form.controls.studentControl.value.id,
          behaviorCodeId: form.controls.behaviorCodeControl.value.id,
          time: form.controls.dateControl.value.getTime(),
          schoolId: this.StorageManager.getLoggedInUser().currentSchoolId,
          behaviorCodeReactionId: this.behaviorCodeReactionIdControl.value?.id,
        });
      });

      this.behaviorCodeEntries =
        await this.behaviorCodeEntryService.batchCreateBehaviorCodeEntries(entries);

      await this.router.navigate(['behavior-code-entries/confirm-reactions'], {
        queryParams: {
          behaviorCodeEntryIds: this.behaviorCodeEntries
            .map((entry) => entry.id)
            .join(',')
        }
      });
    } catch (error: any) {
      this.toastService.error(error.message || 'Unexpected error');
    } finally {
      this.isLoading = false;
      this.dialogRef.close();
    }
  }

  private updateIsSubmitDisabled(): void {
    if (
      this.behaviorCodeReactionIdControl.value == null &&
      this.behaviorCodeReactionOptions?.length
    ) {
      this.isSubmitDisabled = this.behaviorCodeReactionIdControl.invalid;
    } else {
      this.isSubmitDisabled = this.behaviorCodeIdControl.invalid;
    }
  }

  protected readonly PrivilegeEnum = PrivilegeEnum;
}
